import { action, makeAutoObservable, observable, toJS } from "mobx";
import { normalizeRnAiAudio } from "../pages/rn-ai-agent/rnAiAudioNormalize";
import { resolveRnAiAudioUrl } from "../pages/rn-ai-agent/rnAiAudioUrl";

export type RnAiAudioDetail = {
    state: string;
    title: string;
    description: string;
    audio_path: string;
    audio_description_path: string;
    AI_image?: string;
    ai_image?: string;
};

export class AiGentoolsStore {
    id: number;
    tags: any = [];
    title: string;
    tool_search_query: string = "";
    tool_search_results: any = "";
    rootStore: any;
    searchQuery: string = "";
    searchCampaignQuery: string = "";
    add_calendar: boolean = false;
    add_event: boolean = false;
    delete_calendar: boolean = false;
    delete_event: boolean = false;
    add_user_overlay: boolean = false;
    show_event: boolean = false;
    add_user_to_calendar: boolean = false;
    active_calendar: any = {};
    activeEvent: any = {};
    add_checkin: boolean = false;
    add_checkin_manually: boolean = false;
    add_face_modal: boolean = false;
    show_scanned_voter: boolean = false;
    image_capture_modal: boolean = false;
    image_match_modal: boolean = false;
    view_more_booths: boolean = false;
    voter_not_found: boolean = false;
    multiple_faces_detected: boolean = false;
    scanned_voters = [];
    server_error: boolean = false;
    captured_image: any = null;
    show_campaign_overlay: boolean = false;
    show_campaign_status_overlay: boolean = false;
    add_credit_overlay: boolean = false;
    showCampaignFilters: boolean = false;
    showUnitOverlay: boolean = false;
    active_campaign: any = {};
    activeCampaignFilter: any = {};
    image_capture_mode: "faceRecognition" | "voterPhoto" = "faceRecognition";
    voter_photo_voter_id = "";
    voter_photo_s3path: string | null = null;

    // activeEligibiltyDetails: any = {};
    @observable selectedTags: string[] = [];
    @observable createdTags: string[] = [];

    @observable selectedCalendarid: number[] = [];

    @observable activeEligibiltyDetails: any = {};

    @observable rn_ai_audios: RnAiAudioDetail[] = [];
    @observable rn_ai_audios_loading = false;
    @observable rn_ai_audios_error: string | null = null;

    constructor(rootStore: any) {
        makeAutoObservable(this);
        this.rootStore = rootStore;
    }

    @action
    setRnAiAudios = (items: RnAiAudioDetail[]) => {
        this.rn_ai_audios = items;
        this.rn_ai_audios_error = null;
    };

    @action
    setImageCaptureMode = (mode: "faceRecognition" | "voterPhoto") => {
        this.image_capture_mode = mode;
    };

    @action
    setVoterPhotoVoterId = (voterId: string) => {
        this.voter_photo_voter_id = voterId;
    };

    @action
    setVoterPhotoS3Path = (s3Path: string | null) => {
        this.voter_photo_s3path = s3Path;
    };

    @action
    setRnAiAudiosLoading = (value: boolean) => {
        this.rn_ai_audios_loading = value;
    };

    @action
    setRnAiAudiosError = (message: string | null) => {
        this.rn_ai_audios_error = message;
    };

    fetch_rn_ai_audios = async (): Promise<void> => {
        this.setRnAiAudiosLoading(true);
        this.setRnAiAudiosError(null);
        try {
            const res = await this.rootStore.api.get("ai/services/fetch_all");
            if (res.ok && Array.isArray(res.data)) {
                const items = res.data
                    .map((row) => normalizeRnAiAudio(row))
                    .filter((row): row is RnAiAudioDetail => row !== null);
                this.setRnAiAudios(items);
            } else {
                const message =
                    typeof res.data === "string" ? res.data : (res.problem ?? "Failed to load audio list");
                this.setRnAiAudiosError(message);
            }
        } catch (err: any) {
            this.setRnAiAudiosError(err?.message ?? "Failed to load audio list");
        } finally {
            this.setRnAiAudiosLoading(false);
        }
    };

    fetch_rn_ai_audio_url = async (audioPath: string) => {
        return resolveRnAiAudioUrl((bucket, path) => this.rootStore.get_s3_url(bucket, path), audioPath);
    };

    @action
    async get_tool_search(query: string) {
        const payload = {
            keyword: query,
            tags: [],
            published_only: false,
            offset: 0,
            count: 100,
        };
        return await this.rootStore.api.post("ai/gentool/search", payload);
    }

    async generate(instruction, text, options = {}) {
        const payload = {
            text: text,
            display: instruction,
            options: options,
        };
        const response = await this.rootStore.api.post("gen/ask-ai", payload);
        return response.data;
    }

    @action
    update_add_calendar(status) {
        this.add_calendar = status;
    }

    @action
    update_add_event(status) {
        this.add_event = status;
    }

    @action
    update_delete_calendar(status) {
        this.delete_calendar = status;
    }

    @action
    update_delete_event(status) {
        this.delete_event = status;
    }

    @action
    update_add_user_overlay(status) {
        this.add_user_overlay = status;
    }

    @action
    setShowEvent(val) {
        this.show_event = val;
    }

    @action
    setSearchQuery = (val) => {
        this.searchQuery = val;
    };

    @action
    setAddUserToCalendar(val) {
        this.add_user_to_calendar = val;
    }

    async add_project_calendar(payload) {
        return await this.rootStore.api.post("calendar/create_calendar_for_project", payload);
    }

    async add_user_to_Calendar(payload) {
        return await this.rootStore.api.post("calendar/add_calendar_access", payload);
    }

    async delete_user_from_calendar(payload) {
        return await this.rootStore.api.post("calendar/remove_calendar_access", payload);
    }

    async edit_project_calendar(payload) {
        return await this.rootStore.api.post("calendar/update_calendar", payload);
    }

    async delete_calendar_project(payload) {
        return await this.rootStore.api.post("calendar/delete_calendar", payload);
    }

    async delete_event_calendar(payload) {
        return await this.rootStore.api.post("calendar/delete_calendar_event", payload);
    }

    getCalendarTypesData = async (payload) => {
        return await this.rootStore.api.post("calendar/get_calendars_for_project", payload);
    };

    getCalendarEvents = async (payload) => {
        return await this.rootStore.api.post("calendar/get_calendar_events", payload);
    };

    getEventTags = async (payload) => {
        return await this.rootStore.api.post("calendar/get_event_tags", payload);
    };
    getRepoTags = async (payload) => {
        return await this.rootStore.api.post("dropbox/get_tags", payload);
    };

    getEventLocations = async (payload) => {
        return await this.rootStore.api.post("calendar/get_event_locations", payload);
    };

    getVisitorsList = async (payload) => {
        return await this.rootStore.api.post("invite/get_invites", payload);
    };

    async create_event_calendar(payload) {
        return await this.rootStore.api.post("calendar/create_calendar_event", payload);
    }

    async edit_calendar_event(payload) {
        return await this.rootStore.api.post("calendar/update_calendar_event", payload);
    }

    async check_voter_scheme_eligibility(payload) {
        return await this.rootStore.api.post("schemes/get_scheme_details_for_voter", payload);
    }

    async each_scheme_eligiblity_check(payload) {
        return await this.rootStore.api.post("schemes/voter_scheme_eligibility", payload);
    }

    async update_campaign_status(payload) {
        return await this.rootStore.api.post("reachout/change_campaign_status", payload);
    }

    async update_campaign_data(payload) {
        return await this.rootStore.api.post("reachout/edit_campaign", payload);
    }

    async add_credits_data(payload) {
        return await this.rootStore.api.post("reachout/add_credit", payload);
    }

    async check_voter_eligible_schemes(payload) {
        return await this.rootStore.api.post("schemes/get_voters_scheme_details", payload);
    }

    @action
    setActiveCalendar = (val) => {
        this.active_calendar = val;
    };

    @action
    setactiveEvent(val) {
        this.activeEvent = val;
    }

    @action
    setActiveEligibiltyDetails(val) {
        this.activeEligibiltyDetails = val;
    }

    @action
    setSelectedCalendar(id: any) {
        this.selectedCalendarid = [...id];
    }

    @action
    setCreatedTags(tags: string[]) {
        this.createdTags = tags;
    }

    @action
    setSelectedTags(tags: string[]) {
        this.selectedTags = tags;
    }

    @action
    setAddCheckin(val) {
        this.add_checkin = val;
    }

    @action
    setAddCheckinManually(val) {
        this.add_checkin_manually = val;
    }

    @action
    setSearchCampaignQuery = (val) => {
        this.searchCampaignQuery = val;
    };

    @action
    setCampaignFilter = (val) => {
        this.showCampaignFilters = val;
    };

    async add_invitee_list(payload) {
        return await this.rootStore.api.post("invite/save_invite", payload);
    }

    @action
    update_add_face_modal(status) {
        this.add_face_modal = status;
    }

    @action
    update_show_scanned_voter(status) {
        this.show_scanned_voter = status;
    }

    @action
    toggle_image_capture_modal = (status) => {
        this.image_capture_modal = status;
    };

    @action
    toggle_image_match_modal = (status) => {
        this.image_match_modal = status;
    };
    @action
    show_view_more_booths = (status) => {
        this.view_more_booths = status;
    };
    @action
    show_voter_not_found = (status) => {
        this.voter_not_found = status;
    };

    @action
    show_multiple_faces_detected = (status) => {
        this.multiple_faces_detected = status;
    };

    @action
    show_show_campaign_overlay = (status) => {
        this.show_campaign_overlay = status;
    };

    @action
    show_show_campaign_status_overlay = (status) => {
        this.show_campaign_status_overlay = status;
    };

    @action
    show_add_credit_overlay = (status) => {
        this.add_credit_overlay = status;
    };

    @action
    show_unit_group_overlay = (val) => {
        this.showUnitOverlay = val;
    };

    @action
    setActiveCampaign = (campaign) => {
        this.active_campaign = campaign;
    };

    @action
    setScannedVoters(voters) {
        this.scanned_voters = Array.isArray(voters) ? voters : [];
    }

    @action
    show_server_error = (status) => {
        this.server_error = status;
    };

    @action
    setImage = (image) => {
        this.captured_image = image;
    };

    @action
    updateCampaignFilters = (val) => {
        this.activeCampaignFilter = val;
        console.log(val);
    };
}
